import { sanitizeSvgMarkup } from '../../utils/sanitize';

// ── SVG Utilities ────────────────────────────────────────────────────────────
//
// CRITICAL RULES (enforced throughout this file):
//   • svgContent is ALWAYS the original SVG — it is NEVER mutated or stored processed.
//   • Color changes are DOM attribute mutations on a fresh DOMParser tree every render.
//   • All changes are per-element attribute writes — no injected <style> blocks,
//     no global CSS, no regex replacements on raw SVG text.
//   • querySelectorAll('[fill="exact"]') targets only the element carrying that
//     exact value — no false positives, no cross-element bleed.

const SVG_NS = 'http://www.w3.org/2000/svg';

const isShapeTag = (el) => {
  const tag = el.nodeName.toLowerCase().replace(/^svg:/, '');
  return ['path', 'circle', 'rect', 'ellipse', 'polygon', 'polyline', 'line', 'text', 'tspan', 'use'].includes(tag);
};

// ── Default element sub-objects ───────────────────────────────────────────────

/** Default stroke object. Spread this when toggling stroke on for the first time. */
export const DEFAULT_STROKE = {
  enabled:  false,
  color:    '#000000',
  width:    2,
  position: 'center', // 'center' | 'outside' | 'inside'
};

/** Default gradient object. Spread this when toggling gradient on for the first time. */
export const DEFAULT_GRADIENT = {
  enabled:   false,
  mode:      'global',   // 'global' | 'per-color'
  type:      'linear',   // 'linear' | 'radial' | 'angular' | 'diamond'
  colors:    ['#6366f1', '#a855f7'],
  positions: [0, 100],
  opacities: [1, 1],
  angle:     90,
  perColor:  {},         // { [originalColor]: { enabled, type, colors, positions, opacities, angle } }
};

// ── Color extraction (DOMParser-based, exact values) ─────────────────────────

const _validColor = (v) =>
  typeof v === 'string' &&
  v.length > 0 &&
  v !== 'none' &&
  v !== 'transparent' &&
  v !== 'currentColor' &&
  v !== 'inherit' &&
  !v.startsWith('url') &&
  !v.startsWith('{'); // skip template placeholders

/**
 * Extract a `fill` or `stroke` value from an inline `style=""` attribute.
 * Returns the raw color string or null.
 *
 * Robust to multi-declaration styles like `style="fill:#f00;stroke:#000"`,
 * tolerant of whitespace, and case-insensitive on the property name.
 */
const extractStyleColor = (styleStr, prop) => {
  if (!styleStr || typeof styleStr !== 'string') return null;
  // (^|;) anchors to start or after a separator so `fill` inside `flood-fill`
  // never matches.  Capture the value up to ; or end.
  const re = new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]+?)\\s*(?:;|$)`, 'i');
  const m  = styleStr.match(re);
  return m ? m[1].trim() : null;
};

/**
 * Parse the inline `<style>...</style>` blocks of an SVG and return a list of
 * { selector, fill, stroke } entries.  Used both for color extraction and
 * to detect when a fill/stroke is CSS-defined (which inhibits the tint
 * fallback in processSvgForRender).
 *
 * We don't attempt to fully parse CSS — we just look for `fill:` / `stroke:`
 * declarations inside rule bodies and pair them with their selector text.
 */
export const parseSvgStyleRules = (svgDocOrText) => {
  let doc;
  if (typeof svgDocOrText === 'string') {
    try {
      doc = new DOMParser().parseFromString(svgDocOrText, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return [];
    } catch {
      return [];
    }
  } else {
    doc = svgDocOrText;
  }
  const rules = [];
  doc.querySelectorAll('style').forEach((styleEl) => {
    const css = styleEl.textContent || '';
    // Match `selectorList { declarations }` — selector excludes `{` so nested
    // at-rules are skipped but their inner rules still match on the next pass.
    const ruleRe = /([^{}]+)\{([^{}]*)\}/g;
    let m;
    while ((m = ruleRe.exec(css)) !== null) {
      const selector  = m[1].trim();
      const body      = m[2];
      const fill      = extractStyleColor(body, 'fill');
      const stroke    = extractStyleColor(body, 'stroke');
      const stopColor = extractStyleColor(body, 'stop-color');
      if (fill || stroke || stopColor) rules.push({ selector, fill, stroke, stopColor });
    }
  });
  return rules;
};

/**
 * Extract the exact fill / stroke / stop-color values present in an SVG,
 * looking at:
 *   1. element `fill=""` / `stroke=""` / `stop-color=""` attributes
 *   2. inline `style="fill:…;stroke:…;stop-color:…"` attributes
 *   3. `fill:` / `stroke:` / `stop-color:` declarations inside `<style>` blocks
 *
 * Returns an array of unique color strings exactly as they appear in the
 * markup.  Template placeholders like {color}, `none`, `transparent`,
 * `currentColor`, and `url(#…)` references are skipped.
 *
 * Including `stop-color` is what makes gradient recoloring lossless: the
 * caller's colorMap targets the stops by exact value, so the gradient
 * definition (gradientUnits, gradientTransform, spreadMethod, stop offsets)
 * is preserved verbatim — only the colors change.
 */
export const extractSvgColors = (svgText) => {
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return [];
    const found = new Set();

    // 1 + 2: per-element attributes (fill/stroke/stop-color + inline style)
    doc.querySelectorAll('*').forEach((el) => {
      const fill      = el.getAttribute('fill');
      const stroke    = el.getAttribute('stroke');
      const stopColor = el.getAttribute('stop-color');
      if (_validColor(fill))      found.add(fill);
      if (_validColor(stroke))    found.add(stroke);
      if (_validColor(stopColor)) found.add(stopColor);

      const styleAttr = el.getAttribute('style');
      if (styleAttr) {
        const sFill   = extractStyleColor(styleAttr, 'fill');
        const sStroke = extractStyleColor(styleAttr, 'stroke');
        const sStop   = extractStyleColor(styleAttr, 'stop-color');
        if (_validColor(sFill))   found.add(sFill);
        if (_validColor(sStroke)) found.add(sStroke);
        if (_validColor(sStop))   found.add(sStop);
      }
    });

    // 3: <style> rule bodies (fill, stroke, stop-color)
    parseSvgStyleRules(doc).forEach(({ fill, stroke, stopColor }) => {
      if (_validColor(fill))      found.add(fill);
      if (_validColor(stroke))    found.add(stroke);
      if (_validColor(stopColor)) found.add(stopColor);
    });

    return [...found];
  } catch {
    return [];
  }
};

/**
 * Quick check: does the SVG define any fill/stroke via CSS?  Used to inhibit
 * the tint fallback in processSvgForRender — if the author styled the SVG
 * via CSS classes or inline `style=""`, we must not stamp a single `fill`
 * attribute onto every element (it would override the CSS).
 */
export const svgHasCssDefinedFills = (svgText) => {
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return false;

    // <style> blocks with fill/stroke
    if (parseSvgStyleRules(doc).length > 0) return true;

    // Inline style="" with fill/stroke
    const els = doc.querySelectorAll('[style]');
    for (let i = 0; i < els.length; i++) {
      const s = els[i].getAttribute('style') || '';
      if (extractStyleColor(s, 'fill') || extractStyleColor(s, 'stroke')) return true;
    }
    return false;
  } catch {
    return false;
  }
};

// ── Gradient inspection (Properties-panel data source) ──────────────────────

/**
 * For a single <stop> element, return { offset, color, opacity }.
 * Reads attributes first, falls back to inline style="…" — and tolerates the
 * `offset` being expressed as a 0-1 number, a percentage string, or missing.
 */
const _readStop = (stopEl) => {
  const offsetRaw = stopEl.getAttribute('offset') ?? '0';
  // Normalize offset to a percentage number in [0,100] for the UI.
  let offsetPct;
  if (typeof offsetRaw === 'string' && offsetRaw.trim().endsWith('%')) {
    offsetPct = parseFloat(offsetRaw);
  } else {
    const n = parseFloat(offsetRaw);
    // SVG spec: a unitless value is a fraction in [0,1].
    offsetPct = Number.isFinite(n) ? n * 100 : 0;
  }
  if (!Number.isFinite(offsetPct)) offsetPct = 0;
  offsetPct = Math.max(0, Math.min(100, offsetPct));

  const styleAttr = stopEl.getAttribute('style') || '';
  const styleColor   = extractStyleColor(styleAttr, 'stop-color');
  const styleOpacity = (styleAttr.match(/stop-opacity\s*:\s*([^;]+)/i) || [])[1];

  const color   = stopEl.getAttribute('stop-color')   || styleColor   || '#000000';
  const opacity = parseFloat(stopEl.getAttribute('stop-opacity') ?? styleOpacity ?? '1');

  return {
    offset:  offsetPct,
    color:   color.trim(),
    opacity: Number.isFinite(opacity) ? Math.max(0, Math.min(1, opacity)) : 1,
  };
};

/**
 * Compute the angle (in degrees, mathematical convention — 0° points right,
 * positive rotates clockwise on screen because SVG's Y axis points down) of
 * a linear gradient defined by its x1/y1/x2/y2 attributes.
 *
 * Defaults follow the SVG spec: x1=0, y1=0, x2=1, y2=0 → 0°.
 */
const _linearAngleFromCoords = (gradEl) => {
  const num = (a, d) => {
    const v = gradEl.getAttribute(a);
    if (v == null) return d;
    const f = parseFloat(v);
    return Number.isFinite(f) ? f : d;
  };
  const x1 = num('x1', 0), y1 = num('y1', 0);
  const x2 = num('x2', 1), y2 = num('y2', 0);
  const deg = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
  return Math.round(deg * 100) / 100;
};

/**
 * Given an existing linear gradient's x1/y1/x2/y2, return new coords for the
 * requested angle that preserve the original line's midpoint and length.
 * Works for both objectBoundingBox and userSpaceOnUse gradients.
 */
const _linearCoordsForAngle = (gradEl, angleDeg) => {
  const num = (a, d) => {
    const v = gradEl.getAttribute(a);
    if (v == null) return d;
    const f = parseFloat(v);
    return Number.isFinite(f) ? f : d;
  };
  const x1 = num('x1', 0), y1 = num('y1', 0);
  const x2 = num('x2', 1), y2 = num('y2', 0);
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const L  = Math.hypot(x2 - x1, y2 - y1) || 1;
  const rad = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(rad) * L / 2;
  const dy = Math.sin(rad) * L / 2;
  const f = (n) => String(Math.round(n * 10000) / 10000);
  return { x1: f(mx - dx), y1: f(my - dy), x2: f(mx + dx), y2: f(my + dy) };
};

/**
 * Parse every <linearGradient> and <radialGradient> defined in the SVG and
 * return a structured array suitable for a Properties-panel gradient
 * inspector.  Read-only — does not modify the SVG.
 *
 * Returned shape per gradient:
 *   {
 *     id:                'gold',
 *     type:              'linear' | 'radial',
 *     stops:             [{ offset, color, opacity }],
 *     gradientUnits:     'objectBoundingBox' | 'userSpaceOnUse' | null,
 *     gradientTransform: string | null,
 *     spreadMethod:      'pad' | 'reflect' | 'repeat' | null,
 *     // linear-only:
 *     x1, y1, x2, y2, angle,
 *     // radial-only:
 *     cx, cy, r, fx, fy,
 *   }
 *
 * Inheritance via xlink:href / href (Illustrator sometimes splits a gradient
 * into a "template" + a positioned reference) is resolved by merging stops
 * and attributes from the referenced gradient when missing locally.
 */
export const extractSvgGradients = (svgText) => {
  if (typeof svgText !== 'string' || !svgText.trim()) return [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return [];
  } catch {
    return [];
  }

  const all = Array.from(doc.querySelectorAll('linearGradient, radialGradient'));
  // Lookup map for inheritance resolution.
  const byId = new Map();
  all.forEach((g) => { const id = g.getAttribute('id'); if (id) byId.set(id, g); });

  const resolveStops = (gradEl, seen = new Set()) => {
    const id = gradEl.getAttribute('id');
    if (id && seen.has(id)) return [];               // cycle guard
    if (id) seen.add(id);

    const ownStops = Array.from(gradEl.children).filter(
      (c) => c.nodeName.toLowerCase().replace(/^svg:/, '') === 'stop',
    );
    if (ownStops.length > 0) return ownStops.map(_readStop);

    // No local stops — try the xlink:href / href referent
    const href = gradEl.getAttribute('href') || gradEl.getAttribute('xlink:href') || '';
    if (href.startsWith('#')) {
      const ref = byId.get(href.slice(1));
      if (ref) return resolveStops(ref, seen);
    }
    return [];
  };

  return all.map((g) => {
    const localName = g.nodeName.toLowerCase().replace(/^svg:/, '');
    const type      = localName === 'radialgradient' ? 'radial' : 'linear';
    const id        = g.getAttribute('id') || '';
    const stops     = resolveStops(g);
    const base = {
      id,
      type,
      stops,
      gradientUnits:     g.getAttribute('gradientUnits'),
      gradientTransform: g.getAttribute('gradientTransform'),
      spreadMethod:      g.getAttribute('spreadMethod'),
    };
    if (type === 'linear') {
      return {
        ...base,
        x1: g.getAttribute('x1'),
        y1: g.getAttribute('y1'),
        x2: g.getAttribute('x2'),
        y2: g.getAttribute('y2'),
        angle: _linearAngleFromCoords(g),
      };
    }
    return {
      ...base,
      cx: g.getAttribute('cx'),
      cy: g.getAttribute('cy'),
      r:  g.getAttribute('r'),
      fx: g.getAttribute('fx'),
      fy: g.getAttribute('fy'),
    };
  });
};

/**
 * Parse SVG viewBox / width+height for natural aspect ratio.
 * Returns { w, h }.
 */
export const extractSvgDimensions = (svgText) => {
  const vb = svgText.match(/viewBox\s*=\s*["']\s*-?[\d.]+\s+-?[\d.]+\s+([\d.]+)\s+([\d.]+)\s*["']/i);
  if (vb) {
    const w = parseFloat(vb[1]), h = parseFloat(vb[2]);
    if (w > 0 && h > 0) return { w, h };
  }
  const wa = svgText.match(/<svg[^>]+\bwidth\s*=\s*["']([\d.]+)/i);
  const ha = svgText.match(/<svg[^>]+\bheight\s*=\s*["']([\d.]+)/i);
  if (wa && ha) {
    const w = parseFloat(wa[1]), h = parseFloat(ha[1]);
    if (w > 0 && h > 0) return { w, h };
  }
  return { w: 1, h: 1 };
};

/**
 * Parse a raw SVG string and return the canvas element fields needed for
 * the editor state:
 *
 *   svgContent — original SVG string, stored unmodified, always the render source
 *   colors     — unique color values exactly as they appear in the SVG
 *   colorMap   — identity map { originalColor: originalColor }; user edits the values
 *
 * The SVG is stored AS-IS.  No normalisation, no hex conversion.
 */
export const parseSvgForEditor = (rawSvgText) => {
  const colors   = extractSvgColors(rawSvgText);
  const colorMap = Object.fromEntries(colors.map((c) => [c, c]));
  return { svgContent: rawSvgText, colors, colorMap };
};

/**
 * Compute a canvas element size ≤ maxPx that preserves the SVG's natural
 * aspect ratio.
 */
export const svgCanvasSize = (svgText, maxPx = 300) => {
  const { w, h } = extractSvgDimensions(svgText);
  const ratio  = w / h;
  const canvasW = ratio >= 1 ? maxPx : Math.round(maxPx * ratio);
  const canvasH = ratio >= 1 ? Math.round(maxPx / ratio) : maxPx;
  return { width: canvasW, height: canvasH };
};

/**
 * Normalize an SVG string for use as a small thumbnail.
 * - Strips fixed width/height attributes from the root <svg> so the SVG
 *   scales to its container.
 * - Adds a viewBox if missing, derived from any pre-existing width/height
 *   (or 0 0 100 100 as a last-resort fallback).
 * - Sets preserveAspectRatio="xMidYMid meet" when missing.
 *
 * Pure string transform — does NOT mutate the original `svgContent` used
 * by the canvas renderer (callers pass a copy).
 */
export const normalizeSvgForThumbnail = (svgText) => {
  if (typeof svgText !== 'string' || !svgText.includes('<svg')) return svgText;
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return svgText;
    const svg = doc.documentElement;
    if (!svg || svg.nodeName.toLowerCase() !== 'svg') return svgText;

    if (!svg.getAttribute('viewBox')) {
      const w = parseFloat(svg.getAttribute('width')) || 0;
      const h = parseFloat(svg.getAttribute('height')) || 0;
      svg.setAttribute('viewBox', w > 0 && h > 0 ? `0 0 ${w} ${h}` : '0 0 24 24');
    }
    if (!svg.getAttribute('preserveAspectRatio')) {
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');

    doc.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse').forEach(el => {
      const stroke = el.getAttribute('stroke');
      if (stroke && stroke !== 'none' && !el.getAttribute('stroke-width')) {
        el.setAttribute('stroke-width', '2');
      }
    });

    return new XMLSerializer().serializeToString(svg);
  } catch {
    return svgText;
  }
};

// ── Backwards-compatibility shim ──────────────────────────────────────────────

/**
 * Normalise an element to the current field layout.
 *
 * Old layout  (pre-migration):
 *   svgColorMap  : { '#hex': '#hex' }
 *   svgFillGradient : { enabled, type, angle, stops:[] }
 *   stroke       : string ('none' | '#hex')
 *   strokeWidth  : number
 *   strokePosition: string
 *
 * New layout:
 *   colorMap  : { originalColor: currentColor }
 *   colors    : string[]
 *   gradient  : { enabled, type, angle, color1, color2 }
 *   stroke    : { enabled, color, width, position }
 *
 * Idempotent — returns the element unchanged if already in the new layout.
 */
const toNewFormat = (element) => {
  const alreadyNewStroke = element.stroke === undefined || (element.stroke !== null && typeof element.stroke === 'object');
  const alreadyNewColors = element.colorMap !== undefined;
  if (alreadyNewStroke && alreadyNewColors) return element;

  // ── Stroke ──
  const oldStrokeStr = typeof element.stroke === 'string' ? element.stroke : null;
  const oldWidth     = element.strokeWidth || 0;
  const newStroke = alreadyNewStroke && element.stroke !== undefined
    ? element.stroke
    : {
        enabled:  oldStrokeStr !== null && oldStrokeStr !== 'none' && oldWidth > 0,
        color:    oldStrokeStr && oldStrokeStr !== 'none' ? oldStrokeStr : '#000000',
        width:    oldWidth,
        position: element.strokePosition || 'center',
      };

  // ── colorMap / colors ──
  const newColorMap = element.colorMap ?? element.svgColorMap ?? {};
  const newColors   = element.colors   ?? Object.keys(newColorMap);

  // ── gradient ──
  let newGradient;
  if (element.gradient !== undefined) {
    const g = element.gradient;
    // Migrate old format { color1, color2 } → new multi-stop format
    if (g.color1 !== undefined && !Array.isArray(g.colors)) {
      newGradient = {
        enabled:   !!g.enabled,
        mode:      'global',
        type:      g.type  || 'linear',
        colors:    [g.color1 || '#6366f1', g.color2 || '#a855f7'],
        positions: [0, 100],
        opacities: [1, 1],
        angle:     g.angle ?? 90,
        perColor:  {},
      };
    } else {
      newGradient = { ...DEFAULT_GRADIENT, ...g, perColor: g.perColor || {} };
    }
  } else if (element.svgFillGradient) {
    const og = element.svgFillGradient;
    newGradient = {
      enabled:   !!og.enabled,
      mode:      'global',
      type:      og.type  || 'linear',
      colors:    [og.stops?.[0]?.color ?? '#6366f1', og.stops?.[og.stops.length - 1]?.color ?? '#a855f7'],
      positions: [0, 100],
      opacities: [1, 1],
      angle:     og.angle || 90,
      perColor:  {},
    };
  } else {
    newGradient = { ...DEFAULT_GRADIENT };
  }

  return { ...element, colors: newColors, colorMap: newColorMap, gradient: newGradient, stroke: newStroke };
};

// ── DOM helpers ───────────────────────────────────────────────────────────────

const ensureDefs = (svgEl, doc) => {
  let defs = svgEl.querySelector(':scope > defs');
  if (!defs) {
    defs = doc.createElementNS(SVG_NS, 'defs');
    svgEl.insertBefore(defs, svgEl.firstChild);
  }
  return defs;
};

/** CSS gradient angle to SVG linearGradient x1/y1/x2/y2 (objectBoundingBox). */
const angleToSvgCoords = (deg) => {
  const rad = (deg * Math.PI) / 180;
  const f   = (n) => String(Math.round(n * 10000) / 10000);
  return {
    x1: f(0.5 - Math.cos(rad) / 2), y1: f(0.5 - Math.sin(rad) / 2),
    x2: f(0.5 + Math.cos(rad) / 2), y2: f(0.5 + Math.sin(rad) / 2),
  };
};

// ── Rendering pipeline steps ──────────────────────────────────────────────────

// Property names whose value side we rewrite in styles / inline style attrs.
// stop-color is included so gradients are recolored IN PLACE — gradientUnits,
// gradientTransform, spreadMethod, and stop offsets are all preserved.
const _COLOR_PROPS = ['fill', 'stroke', 'stop-color'];
const _COLOR_PROPS_RE = _COLOR_PROPS.join('|');

/**
 * Substitute fill/stroke/stop-color values inside a `style="…"` declaration
 * string, matching exactly the same color values applyColorMap looks for.
 * Returns the rewritten style string (or the original if nothing changed).
 */
const substituteStyleColors = (styleStr, colorMap) => {
  if (!styleStr) return styleStr;
  let out = styleStr;
  Object.entries(colorMap).forEach(([original, current]) => {
    if (original === current) return;
    const esc = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(
      new RegExp(`((?:^|;)\\s*(?:${_COLOR_PROPS_RE})\\s*:\\s*)${esc}(\\s*(?:;|$))`, 'gi'),
      `$1${current}$2`
    );
  });
  return out;
};

/**
 * Substitute fill/stroke/stop-color values inside <style> rule bodies.
 * Targets only the VALUE side of declarations — selector text is never
 * rewritten, so class names that happen to look like colors survive intact.
 */
const substituteStyleBlocks = (svgEl, colorMap) => {
  svgEl.querySelectorAll('style').forEach((styleEl) => {
    const css = styleEl.textContent || '';
    let next = css;
    Object.entries(colorMap).forEach(([original, current]) => {
      if (original === current) return;
      const esc = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match the property's value position only — anchored by `:` on the
      // left and `;` or `}` on the right.
      next = next.replace(
        new RegExp(`((?:${_COLOR_PROPS_RE})\\s*:\\s*)${esc}(\\s*(?:;|\\}))`, 'gi'),
        `$1${current}$2`
      );
    });
    if (next !== css) styleEl.textContent = next;
  });
};

/**
 * Apply colorMap to a live SVG DOM tree.
 *
 * Targets every color-carrying surface — all addressed exactly:
 *   1. `fill="…"` / `stroke="…"` / `stop-color="…"` attributes (querySelectorAll)
 *   2. Inline `style="fill:…;stroke:…;stop-color:…"` declarations
 *   3. `<style>` rule bodies with `fill:` / `stroke:` / `stop-color:` declarations
 *
 * No element is touched unless its color value EXACTLY matches a colorMap key.
 * Gradient elements (<linearGradient>/<radialGradient>) and their attributes
 * (gradientUnits, gradientTransform, spreadMethod, stop offsets) are NEVER
 * mutated — only the colors inside <stop>s change.
 */
const applyColorMap = (svgEl, colorMap) => {
  // 1. Direct attributes — fill, stroke, and stop-color
  Object.entries(colorMap).forEach(([original, current]) => {
    if (original === current) return;
    svgEl.querySelectorAll(`[fill="${original}"]`).forEach((el)        => el.setAttribute('fill',       current));
    svgEl.querySelectorAll(`[stroke="${original}"]`).forEach((el)      => el.setAttribute('stroke',     current));
    svgEl.querySelectorAll(`[stop-color="${original}"]`).forEach((el)  => el.setAttribute('stop-color', current));
  });
  // 2. Inline style="…" — walk once, rewrite if needed
  svgEl.querySelectorAll('[style]').forEach((el) => {
    const before = el.getAttribute('style');
    const after  = substituteStyleColors(before, colorMap);
    if (after !== before) el.setAttribute('style', after);
  });
  // 3. <style> rule bodies
  substituteStyleBlocks(svgEl, colorMap);
};

/**
 * Build an SVG gradient element (<linearGradient> or <radialGradient>) from
 * the GradientEditor data format { type, colors, positions, opacities, angle }.
 */
const buildSvgGradientEl = (doc, config, gradId) => {
  const { type = 'linear', colors = [], positions = [], opacities = [], angle = 90 } = config;

  let gradEl;
  if (type === 'radial' || type === 'diamond') {
    gradEl = doc.createElementNS(SVG_NS, 'radialGradient');
    ['cx', 'cy', 'r', 'fx', 'fy'].forEach((a) => gradEl.setAttribute(a, '50%'));
  } else {
    // linear, angular (SVG has no conic — fall back to linear)
    gradEl = doc.createElementNS(SVG_NS, 'linearGradient');
    const { x1, y1, x2, y2 } = angleToSvgCoords(angle);
    gradEl.setAttribute('x1', x1);
    gradEl.setAttribute('y1', y1);
    gradEl.setAttribute('x2', x2);
    gradEl.setAttribute('y2', y2);
  }
  gradEl.setAttribute('id', gradId);
  gradEl.setAttribute('gradientUnits', 'objectBoundingBox');

  colors.forEach((color, i) => {
    const stop = doc.createElementNS(SVG_NS, 'stop');
    stop.setAttribute('offset', `${positions[i] ?? (i === 0 ? 0 : 100)}%`);
    stop.setAttribute('stop-color', color);
    const opacity = opacities[i] ?? 1;
    if (opacity < 1) stop.setAttribute('stop-opacity', String(opacity));
    gradEl.appendChild(stop);
  });
  return gradEl;
};

/**
 * Global gradient — applies one gradient to every visible fill in the SVG.
 * Runs AFTER applyColorMap.
 */
const applyGlobalGradient = (svgEl, doc, gradient, elementId) => {
  if (!gradient?.enabled || gradient.mode === 'per-color') return;

  const gradId = `_svgg-${elementId || 'el'}`;
  const defs   = ensureDefs(svgEl, doc);
  const prev   = defs.querySelector(`#${gradId}`);
  if (prev) defs.removeChild(prev);

  defs.appendChild(buildSvgGradientEl(doc, gradient, gradId));

  svgEl.querySelectorAll('*').forEach((el) => {
    if (!isShapeTag(el)) return;
    const fill = el.getAttribute('fill');
    if (fill === 'none' || fill === 'transparent' || (fill && fill.startsWith('url'))) return;
    el.setAttribute('fill', `url(#${gradId})`);
  });
};

/**
 * Per-color gradient — each original color can have its own gradient applied
 * only to the shapes that carry that color.  Shapes without a per-color gradient
 * keep whatever color applyColorMap assigned them.
 */
const applyPerColorGradient = (svgEl, doc, gradient, colorMap, elementId) => {
  if (!gradient?.enabled || gradient.mode !== 'per-color') return;

  const defs     = ensureDefs(svgEl, doc);
  const perColor = gradient.perColor || {};

  Object.entries(perColor).forEach(([originalColor, perColorGrad], idx) => {
    if (!perColorGrad?.enabled) return;

    // Target the current color (after colorMap substitution)
    const currentColor = colorMap?.[originalColor] || originalColor;
    const gradId       = `_svgpc-${elementId || 'el'}-${idx}`;
    const prev         = defs.querySelector(`#${gradId}`);
    if (prev) defs.removeChild(prev);

    defs.appendChild(buildSvgGradientEl(doc, perColorGrad, gradId));

    svgEl.querySelectorAll(`[fill="${currentColor}"]`).forEach((el) => {
      el.setAttribute('fill', `url(#${gradId})`);
    });
    svgEl.querySelectorAll(`[stroke="${currentColor}"]`).forEach((el) => {
      el.setAttribute('stroke', `url(#${gradId})`);
    });
  });
};

/**
 * Apply stroke per shape element — colour, width, and position.
 *
 * position values:
 *   'center'  — standard centered stroke (SVG default)
 *   'outside' — double stroke-width + paint-order="stroke fill" so fill overpaints
 *               the inner half, making the full visible stroke appear outside the path
 *   'inside'  — double stroke-width + a per-shape <clipPath> that clones the fill
 *               region, clipping the outer half of the stroke away
 *
 * All changes are attribute-level on shape elements only.  No <style> injection.
 */
const applyStroke = (svgEl, doc, stroke, elementId) => {
  if (!stroke?.enabled || stroke.width <= 0) return;

  const pos  = stroke.position || 'center';
  const defs = pos === 'inside' ? ensureDefs(svgEl, doc) : null;

  svgEl.querySelectorAll('*').forEach((el, idx) => {
    if (!isShapeTag(el)) return;
    el.setAttribute('stroke', stroke.color);

    if (pos === 'center') {
      el.setAttribute('stroke-width', String(stroke.width));
      el.removeAttribute('paint-order');
      el.removeAttribute('clip-path');

    } else if (pos === 'outside') {
      el.setAttribute('stroke-width', String(stroke.width * 2));
      el.setAttribute('paint-order', 'stroke fill');
      el.removeAttribute('clip-path');

    } else if (pos === 'inside') {
      const fill = el.getAttribute('fill');
      // Lines and unfilled shapes fall back to center — they have no fill region to clip to
      const tag  = el.nodeName.toLowerCase().replace(/^svg:/, '');
      if (fill === 'none' || fill === 'transparent' || tag === 'line') {
        el.setAttribute('stroke-width', String(stroke.width));
        return;
      }
      // Don't clobber an author-defined clip-path: inside-stroke simulation
      // would overwrite it and destroy the original masking region.  Fall back
      // to center stroke for these shapes — fidelity wins over stroke style.
      if (el.hasAttribute('clip-path')) {
        el.setAttribute('stroke-width', String(stroke.width));
        el.removeAttribute('paint-order');
        return;
      }
      el.setAttribute('stroke-width', String(stroke.width * 2));
      el.removeAttribute('paint-order');

      const clipId    = `_svgc-${elementId || 'el'}-${idx}`;
      const clipPath  = doc.createElementNS(SVG_NS, 'clipPath');
      clipPath.setAttribute('id', clipId);
      const clipShape = el.cloneNode(true);
      ['stroke', 'stroke-width', 'clip-path', 'paint-order'].forEach((a) => clipShape.removeAttribute(a));
      clipPath.appendChild(clipShape);
      defs.appendChild(clipPath);
      el.setAttribute('clip-path', `url(#${clipId})`);
    }
  });
};

/**
 * Apply user edits (from the Properties-panel gradient inspector) to the
 * gradients that already exist inside the SVG.  Targets each gradient by id.
 *
 * Shape of `gradientEdits`:
 *   {
 *     [gradientId]: {
 *       stops?:             [{ offset, color, opacity }],  // overrides ALL stops
 *       angle?:             number,        // linear gradients only — preserves
 *                                          // the original line midpoint+length
 *       gradientTransform?: string | null, // null → remove
 *       gradientUnits?:     string | null,
 *       spreadMethod?:      string | null,
 *     }
 *   }
 *
 * Critical fidelity guarantees:
 *   • Only attributes/children of the matching <linearGradient>/<radialGradient>
 *     are touched.  Nothing else in the SVG is mutated.
 *   • If `gradientEdits` is empty/undefined, this function is a no-op — render
 *     behavior is bit-identical to before.
 *   • Stop offsets are written back as percentages exactly as the inspector
 *     stored them, so round-tripping (read → write → read) is stable.
 *   • Existing `gradientUnits`, `gradientTransform`, `spreadMethod`, and
 *     gradient placement attributes (cx/cy/r/fx/fy on radial) are preserved
 *     unless the edit explicitly overrides them.
 */
const applyGradientEdits = (svgEl, doc, gradientEdits) => {
  if (!gradientEdits || typeof gradientEdits !== 'object') return;
  const entries = Object.entries(gradientEdits);
  if (entries.length === 0) return;

  entries.forEach(([id, edit]) => {
    if (!edit || typeof edit !== 'object') return;
    // We can't use CSS.escape inside the parsed document directly via
    // querySelector("#id") if the id contains special chars; iterate instead.
    const grad = Array.from(svgEl.querySelectorAll('linearGradient, radialGradient'))
      .find((g) => g.getAttribute('id') === id);
    if (!grad) return;

    const localName = grad.nodeName.toLowerCase().replace(/^svg:/, '');
    const isLinear  = localName === 'lineargradient';

    // ── 1. Stops ─────────────────────────────────────────────────────────
    if (Array.isArray(edit.stops)) {
      // Remove existing direct-child <stop>s only — leave anything else (e.g.
      // an <animate>, though rare) untouched.
      Array.from(grad.children).forEach((c) => {
        if (c.nodeName.toLowerCase().replace(/^svg:/, '') === 'stop') {
          grad.removeChild(c);
        }
      });
      edit.stops.forEach((s) => {
        const stop = doc.createElementNS(SVG_NS, 'stop');
        const off  = Number.isFinite(s.offset) ? s.offset : 0;
        stop.setAttribute('offset', `${Math.max(0, Math.min(100, off))}%`);
        stop.setAttribute('stop-color', String(s.color || '#000000'));
        const op = Number.isFinite(s.opacity) ? s.opacity : 1;
        if (op < 1) stop.setAttribute('stop-opacity', String(Math.max(0, Math.min(1, op))));
        grad.appendChild(stop);
      });
    }

    // ── 2. Linear angle (preserves line midpoint + length) ───────────────
    if (isLinear && Number.isFinite(edit.angle)) {
      const next = _linearCoordsForAngle(grad, edit.angle);
      grad.setAttribute('x1', next.x1);
      grad.setAttribute('y1', next.y1);
      grad.setAttribute('x2', next.x2);
      grad.setAttribute('y2', next.y2);
    }

    // ── 3. Explicit attribute overrides (rare from the panel today) ──────
    const setOrClear = (name, val) => {
      if (val == null) grad.removeAttribute(name);
      else             grad.setAttribute(name, String(val));
    };
    if (Object.prototype.hasOwnProperty.call(edit, 'gradientTransform')) setOrClear('gradientTransform', edit.gradientTransform);
    if (Object.prototype.hasOwnProperty.call(edit, 'gradientUnits'))     setOrClear('gradientUnits',     edit.gradientUnits);
    if (Object.prototype.hasOwnProperty.call(edit, 'spreadMethod'))      setOrClear('spreadMethod',      edit.spreadMethod);
  });
};

// ── Main render function ──────────────────────────────────────────────────────

/**
 * Process a canvas SVG element into an HTML string ready for
 * dangerouslySetInnerHTML.
 *
 * Pipeline (all steps work on a fresh DOM tree parsed from element.svgContent):
 *   1. Compat-shim: normalise old-format elements to new field layout
 *   2. Replace {color} template (Memphis SVGs) before DOMParser
 *   3. Parse to DOM with DOMParser
 *   4. Ensure viewBox exists + set width/height="100%"
 *   5. applyColorMap  — per-attribute exact replacement
 *   6. applyGradient  — injects <linearGradient>/<radialGradient>, applies fill
 *   7. applyStroke    — per-element attribute write, handles position simulation
 *   8. XMLSerializer → HTML string
 *
 * element.svgContent is NEVER modified.
 */
export const processSvgForRender = (rawElement) => {
  const element = toNewFormat(rawElement);
  let raw = element.svgContent || '';
  if (!raw.trim()) return '';

  // Memphis SVGs use {color} template — substitute before DOMParser
  // (DOMParser would treat {color} as a literal attribute value)
  if (raw.includes('{color}')) {
    raw = raw.replace(/{color}/g, element.fill || '#6366f1');
  }

  // ── Parse ─────────────────────────────────────────────────────────────────
  let svgEl, doc;
  try {
    doc = new DOMParser().parseFromString(raw, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return sanitizeSvgMarkup(raw);
    svgEl = doc.documentElement;
    if (!svgEl || svgEl.nodeName.toLowerCase() !== 'svg') return sanitizeSvgMarkup(raw);
  } catch {
    return sanitizeSvgMarkup(raw);
  }

  // ── Ensure viewBox so SVG scales to its container ─────────────────────────
  if (!svgEl.getAttribute('viewBox')) {
    const w = parseFloat(svgEl.getAttribute('width')  || '0');
    const h = parseFloat(svgEl.getAttribute('height') || '0');
    if (w > 0 && h > 0) svgEl.setAttribute('viewBox', `0 0 ${w} ${h}`);
  }
  svgEl.setAttribute('width',  '100%');
  svgEl.setAttribute('height', '100%');

  // ── Apply colorMap ────────────────────────────────────────────────────────
  const colorMap = element.colorMap || {};
  if (Object.keys(colorMap).length > 0) {
    applyColorMap(svgEl, colorMap);
  } else if (
    !raw.includes('{color}') &&
    element.fill &&
    element.fill !== 'none' &&
    !svgHasCssDefinedFills(raw)            // Bug #2: skip when CSS owns the fills
  ) {
    // Tint fallback: SVG has no detectable colors and no CSS-defined fills —
    // apply element.fill to genuinely un-painted shapes only.  This path used
    // to overwrite EVERY shape's fill, which destroyed gradient/metallic
    // effects whenever colors were declared in <style> or via inline `style="…"`.
    //
    // Safe-tint rules:
    //   • Only touch shape elements (path, rect, circle, …) — never <stop>,
    //     <feFlood>, gradients, etc.
    //   • Never override an existing attribute fill.
    //   • Never override an inline style="fill:…".
    //   • Never override `none`, `transparent`, or `url(#…)` references.
    //   • Skip elements that carry a `class` (they may inherit a CSS fill we
    //     can't statically resolve).
    const tint = element.fill;
    svgEl.querySelectorAll('*').forEach((el) => {
      if (!isShapeTag(el)) return;
      const fill      = el.getAttribute('fill');
      const styleFill = extractStyleColor(el.getAttribute('style'), 'fill');
      if (fill === 'none' || fill === 'transparent') return;
      if (fill && fill.startsWith('url')) return;
      if (styleFill) return;
      if (fill) return;                    // already painted by attribute
      if (el.hasAttribute('class')) return; // class may carry a CSS fill
      el.setAttribute('fill', tint);
    });
  }

  // ── Per-gradient edits from the Properties-panel inspector ────────────────
  // Runs AFTER colorMap so that explicit per-stop edits win over the global
  // recolor pipeline.  No-op if element.gradientEdits is missing/empty.
  applyGradientEdits(svgEl, doc, element.gradientEdits);

  // ── Gradient (runs after colorMap to allow intentional override) ──────────
  if (element.gradient?.mode === 'per-color') {
    applyPerColorGradient(svgEl, doc, element.gradient, element.colorMap, element.id);
  } else {
    applyGlobalGradient(svgEl, doc, element.gradient, element.id);
  }

  // ── Stroke ────────────────────────────────────────────────────────────────
  applyStroke(svgEl, doc, element.stroke, element.id);

  // The markup comes from uploads and saved designs and is inserted into the page:
  // sanitise it (no scripts, event handlers or foreignObject — utils/sanitize.js sanitizeSvgMarkup).
  return sanitizeSvgMarkup(new XMLSerializer().serializeToString(svgEl));
};
